import React from 'react';
import { CardHeader, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';

/**
 * Loading placeholder for the dashboard's main content. Mirrors
 * NewDashboard.tsx's actual current structure: a header row (title/date +
 * the DateRangePicker/member-select controls), then ONE seamless bordered
 * block with hairline dividers holding two 1/2/1 lg-column rows of widgets
 * (TopModelsUsed | TokenSpendOverTime | TopProjects, then NeedsAttentionCard
 * | PeopleAndAgentsPreview | AgentsCostPanel) -- not separate floating Cards.
 *
 * There used to be a "Daily Summary" card between the header and the KPI
 * grid; it was removed from NewDashboard.tsx (2026-08-28 the section itself
 * was hidden, then deleted outright) and this file wasn't updated to match,
 * so the loading state briefly showed a card that no longer exists in the
 * real layout. Fixed here -- no daily-summary block anymore.
 *
 * Each real widget already renders its own shimmer skeleton once mounted
 * (see TopModelsUsed.tsx/TopProjects.tsx/TokenSpendOverTime.tsx/
 * NeedsAttentionCard.tsx/PeopleAndAgentsPreview.tsx/AgentsCostPanel.tsx's own
 * `loading` branches), so this component only covers the brief window
 * before team access/membership is confirmed and those widgets exist at
 * all. The per-cell skeletons below are deliberately generic (a title bar +
 * a few content rows/a chart-shaped block) rather than copies of each
 * widget's exact skeleton, so this file doesn't need to stay in lockstep
 * with six other components' internals -- just the grid shape.
 *
 * Uses the semantic theme tokens (bg-background/border-border) so it
 * matches in both the light (paper) and dark (ink) themes. Rendered
 * standalone (Dashboard.tsx, before team access is known -- no
 * sidebar/topbar there yet) or inside the real sidebar/topbar shell
 * (TeamDashboard.tsx).
 */

const WidgetCellSkeleton: React.FC<{ tall?: boolean; rows?: number }> = ({ tall, rows = 3 }) => (
  <div className="flex h-full flex-col">
    <CardHeader className="space-y-2">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-44" />
    </CardHeader>
    <div className="border-t border-border" />
    <CardContent className="flex flex-1 flex-col space-y-3 pt-4">
      {tall ? (
        <Skeleton className="h-[220px] w-full" />
      ) : (
        Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-3 w-full" />
          </div>
        ))
      )}
    </CardContent>
  </div>
);

const SkeletonLoader: React.FC = () => {
  return (
    <div className="max-w-[1800px] mx-auto w-full">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          <Skeleton className="h-10 w-full sm:w-64" />
          <Skeleton className="h-10 w-full sm:w-48" />
        </div>
      </div>

      {/* One seamless bordered block with hairline dividers -- matches
          NewDashboard's "divide-y divide-border overflow-hidden border
          border-border bg-background" grid, not separate floating Cards. */}
      <div className="divide-y divide-border overflow-hidden border border-border bg-background shadow-sm">
        <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-4 lg:divide-x lg:divide-y-0">
          <div className="lg:col-span-1">
            <WidgetCellSkeleton />
          </div>
          <div className="lg:col-span-2">
            <WidgetCellSkeleton tall />
          </div>
          <div className="lg:col-span-1">
            <WidgetCellSkeleton />
          </div>
        </div>
        <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-4 lg:divide-x lg:divide-y-0">
          <div className="lg:col-span-1">
            <WidgetCellSkeleton rows={2} />
          </div>
          <div className="lg:col-span-2">
            <WidgetCellSkeleton rows={3} />
          </div>
          <div className="lg:col-span-1">
            <WidgetCellSkeleton rows={2} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkeletonLoader;
